import {
  AbTestType,
  CarOwnerShipType,
  DocumentType,
  DossierGlobalOrigins,
  NotificationType,
  UsersRole,
} from '../dossier';
import { EmailType } from '../email';
import { FeatureName } from '../features';
import { FilterDisplayStyle } from './appointment-filters';
import { FeatureBrandConfiguration } from './brand-feature-configuration';
import { CallConfig } from './call-config';
import { CustomTagManagerData } from './custom-tag-manager-data';
import { CutOffOptions } from './cut-off-options';
import { DictionaryName } from './dictionary-name';
import { GlassUsageConfiguration } from './glass-usage';
import { GoogleTagManagerData } from './google-tag-manager-data';
import { InterventionDuration } from './intervention-duration';
import { lang } from './lang';
import { LoggerLevelType } from './logger-level-enum';
import { MapConfig } from './map-config';
import { MatomoData } from './matomo-data';
import { PatternsConfig } from './patterns-config';
import { Restrictions } from './restrictions';
import { RoutingName } from './routing-name';
import { Supervisor } from './supervisor';
import { ThemeName, ThemeOptions } from './theme-name';

export interface Configuration {
  id: string;
  insurerName: string;
  dns: string[];
  website: string;
  phone: string;
  accessibleLink?: string;
  gdpr: {
    legalUri: string;
    anonymisationDelay?: number;
  };
  sitePlan: string;
  legalMentions: string;
  gcu?: string;
  securityLink?: string;
  cookiesLink?: string;
  cookiesSettings?: string;
  notAffiliatedLink?: string;
  footerLogos: string[];
  restrictions?: Restrictions;
  map: MapConfig;
  fitterBrands: FeatureBrandConfiguration[];
  approvedPartners?: string[];
  agendaSearch?: AgendaSearchConfig;
  fitterScoringVersions: {
    customer: string;
    insurer: string;
  };
  emailTemplates: {
    emailSender: string;
    insurerDuplicatedBookMail: InsurerDuplicatedBookMail[];
    emailsCC: string[];
    templatesId: { [key in EmailType]: string };
    templatesVariable: { [key: string]: string };
    templateStyles: {
      primaryColor: string;
      secondaryColor: string;
      tertiaryColor: string;
      warnColor: string;
      borderRadius: boolean;
      headerLogo: string;
      headerCoLogo?: string;
      footerLogo: string;
      bottomVisual: string;
      headerColor: string;
      headerTextColor: string;
    };
  };
  notifications: {
    smsService?: SmsServiceType;
    smsFrom?: string;
    messagingServiceSid?: string;
  };
  call: CallConfig;
  features: FeatureName[];
  theme: ThemeName;
  themeOptions?: ThemeOptions;
  dictionary: DictionaryName;
  defaultLang: lang;
  supportedLang: lang[];
  uniqueTimezone: string;
  supportedDocuments: DocumentType[];
  assetsFolder: string;
  assetsData: AssetsData;
  defaultInterventionDuration?: InterventionDuration;
  cutOffOptions: CutOffOptions;
  abandonComment?: AbandonComment;
  excessOptions: ExcessOptions;
  patterns: PatternsConfig;
  preferredNotificationList: NotificationType[];
  insuranceSuggestion: InsuranceSuggestion[];
  googleTagManagerData?: GoogleTagManagerData;
  google_conversion_partner_id?: string;
  customTagManagerData?: CustomTagManagerData;
  matomoData?: MatomoData;
  htmlDomOptions?: HtmlDomOptions;
  appointmentSearch: AppointmentSearchConfig;
  timeFilters: TimeFilters[];
  filterStyle?: {
    b2c: FilterDisplayStyle;
    b2b: FilterDisplayStyle;
  };
  originFilters: OriginFilter[];
  glassUsage: GlassUsageConfiguration;
  glassUsageInsurer?: GlassUsageConfiguration;
  carOwnerShipTypes: CarOwnerShipTypes;
  errorReportingUrl: string;
  rolesList: UsersRole[];
  loggerLevel: LoggerLevelType;
  partProviders: string[];
  cancelComment: CancelComment;
  supervisors: Supervisor[];
  confirmMeetingEmail?: string;
  stepperPageMapping: { stepKey: string; pages: RoutingName[] }[];
  autoLogoutDelay?: AutoLogoutDelayConfig[];
  avoidDuplicateDelay?: number;
  abTestingRates?: { [key in AbTestType]?: number };
}

export interface AutoLogoutDelayConfig {
  group?: string;
  delay: number;
}

export type PublicConfiguration = Omit<
  Configuration,
  | 'dns'
  | 'supervisors'
  | 'appointmentSearch'
  | 'rolesList'
  | 'emailTemplates'
  | 'notifications'
  | 'preferences'
  | 'fitterScoringVersions'
  | 'agendaSearch'
>;

export interface AppointmentSearchConfig {
  threshold: AppointmentThresholdSize;
  maximumDistSearch: number;
}

export interface AgendaSearchConfig {
  mobilityStrategy: 'zones-earliest' | 'nearest';
  selectExactPostalCode?: boolean;
}

export interface InsurerDuplicatedBookMail {
  email: string;
  env: string[];
}

export interface CarOwnerShipTypes {
  carOwnerShipTypesList: CarOwnerShipType[];
}

export type AppointmentThresholdSize =
  | 'small'
  | 'standard'
  | 'large'
  | 'superlarge';

export interface InsuranceSuggestion {
  insuranceName: string;
  insuranceCode: number;
  internalInsuranceCode: number | string;
  insuranceCountryCode: number;
  id?: string;
  //TODO : make required and update other conf ids (insuranceName?.replace(/[^a-zA-Z0-9 ]/g, '')?.split(' ')?.join('_')?.toLocaleLowerCase())
}

export interface AbandonComment {
  insurer?: {
    allowFreeText: boolean;
    options: string[];
  };
}

export interface ExcessOptions {
  defaultExcess: string;
  excessList: excessList;
}

export interface excessList {
  excess0: string[];
  excessWithValue: string[];
  proportionalExcess: string[];
  operationTypeExcess?: { repair: string; replace: string };
}

export interface TimeFilters {
  start: number;
  end: number;
}

export interface OriginFilter {
  lib: DossierGlobalOrigins;
  origin: string;
  businessOrigin: string;
  options?: {
    displayMainLogo?: boolean;
    displayCoLogo?: boolean;
    forceInsuranceCode?: string;
  };
}

export type SmsServiceType = 'twilio' | 'all-my-sms';

export const AssetPath = {
  ADAS: 'ADAS',
  VEHICLE_TYPE: 'VEHICLE-TYPE',
  IN_VISION_FIELD: 'IN-VISION-FIELD',
  REPAIR: 'REPAIR',
  IMPACT: 'IMPACT',
  USER_CAN_BOOK: 'USER-CAN-BOOK',
  USER_CANNOT_BOOK: 'USER-CANNOT-BOOK',
  SUMMARY: 'SUMMARY',
  PRE_DECLARATION: 'PRE-DECLARATION',
  OPEN_ARRIVAL: 'OPEN-ARRIVAL',
  OPEN_ARRIVAL_MOBILE: 'OPEN-ARRIVAL-MOBILE',
  CUSTOMER_INFOS: 'CUSTOMER-INFOS',
  CUSTOMER_INFOS_MOBILE: 'CUSTOMER-INFOS-MOBILE',
  MODIFICATION: 'MODIFICATION',
  CANCEL: 'CANCEL',
  LICENSE_PLATE: 'LICENSE-PLATE',
  APMT_VALIDATION: 'APMT-VALIDATION',
  APMT_SUGGESTION_VALIDATION: 'APMT-SUGGESTION-VALIDATION',
} as const;
export type AssetPath = typeof AssetPath[keyof typeof AssetPath];

export interface AssetsData {
  default: string;
  defaultMobile: string;
  specifics: Partial<Record<AssetPath, string>>;
}

export interface CallableScript {
  id: string;
  url: string;
  env: string[];
}

export interface HtmlDomOptions {
  callableScripts?: CallableScript[];
  metaInjection?: MetaInjection[];
}

export interface MetaInjection {
  name: string;
  content: string;
  env: string[];
  pages: RoutingName[];
  title?: string;
}

export interface CancelComment {
  allowOther: boolean;
  reasons: string[];
}
